/**
 * API client.
 *
 * The session lives in an httpOnly cookie, so every request sends credentials
 * and no token is ever handled in JavaScript. `X-Requested-With` satisfies the
 * server's CSRF origin guard for requests that carry no Origin header.
 */

import i18n from '../i18n';

export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown>; requestId?: string };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function requestEnvelope<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'chatflow-web',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const err = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(
      response.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? `Request failed with status ${response.status}`,
      err?.details,
    );
  }

  return payload as T;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const payload = await requestEnvelope<{ data: T }>(method, path, body);
  return payload.data;
}

/** GETs a non-JSON resource (CSV export) as text, with the same error shape. */
export async function requestText(path: string): Promise<{ text: string; filename: string | null }> {
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'chatflow-web' },
  });
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const err = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(response.status, err?.code ?? 'UNKNOWN', err?.message ?? 'Request failed');
  }
  const disposition = response.headers.get('content-disposition');
  const filename = /filename="([^"]+)"/.exec(disposition ?? '')?.[1] ?? null;
  return { text: await response.text(), filename };
}

/** GETs a binary resource (invoice PDF) and hands it to the browser as a download. */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'chatflow-web' },
  });
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const err = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(response.status, err?.code ?? 'UNKNOWN', err?.message ?? 'Request failed');
  }
  const disposition = response.headers.get('content-disposition');
  const filename = /filename="([^"]+)"/.exec(disposition ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  // Revoked later, not now: Firefox and Safari start the download after the
  // click returns, and a URL revoked by then downloads nothing.
  setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_TTL_MS);
}

const BLOB_URL_TTL_MS = 60_000;

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

// --- resource types (Phase 1 surface) ---------------------------------------

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string | null;
  avatarUrl: string | null;
  emailVerified: boolean;
  createdAt: string;
}

export type WorkspaceRole = 'owner' | 'admin' | 'agent';

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  role: WorkspaceRole;
  createdAt: string;
}

/**
 * `GET /workspaces/current` (D-183): the workspace, its suspension and the
 * plan's features. Answers while the workspace is suspended, to every member.
 * `upgradeFor` names, for each feature the plan turns off, the cheapest
 * monthly plan that opens it (null: open, or no plan opens it) — never a price.
 */
export interface CurrentWorkspace extends Workspace {
  suspendedAt: string | null;
  suspensionReason: string | null;
  plan: {
    code: string;
    name: string;
    features: PlanFeatures;
    maxAnalyticsDays: number | null;
    upgradeFor: Record<PlanFeatureKey, UpgradeTarget | null>;
  };
}

/** The plan that opens a feature, as `plan.upgradeFor` names it (D-183): no price. */
export interface UpgradeTarget {
  code: string;
  name: string;
}

export interface Member {
  userId: string;
  email: string;
  firstName: string;
  lastName: string | null;
  role: WorkspaceRole;
  status: string;
  hasInboxSeat: boolean;
  joinedAt: string;
}

export interface Invite {
  id: string;
  email: string;
  role: WorkspaceRole;
  expiresAt: string;
  createdAt: string;
}

/** What the acceptance page knows before the click (D-183): the INVITING workspace, not the reader's. */
export interface InvitePreview {
  workspaceName: string;
  /** The active owner's display name; null when the workspace has none. */
  ownerName: string | null;
  /** True when accepting would be refused (402 `maxUsers`). */
  full: boolean;
  /** The inviting workspace's user limit; null = unlimited. */
  limit: number | null;
}

export interface Session {
  user: User;
  activeWorkspaceId: string | null;
}

/** What the public forms need before rendering: the Turnstile site key, or null when the challenge is off (D-160). */
export interface AuthConfig {
  turnstileSiteKey: string | null;
}

/** The solved Turnstile challenge, when the form showed one. */
export interface CaptchaInput {
  turnstileToken?: string;
}

export const authApi = {
  config: () => api.get<AuthConfig>('/auth/config'),
  me: () => api.get<Session>('/auth/me'),
  login: (email: string, password: string, captcha: CaptchaInput = {}) =>
    api.post<Session>('/auth/login', { email, password, ...captcha }),
  logout: () => api.post<{ ok: true }>('/auth/logout'),
  register: (input: { email: string; password: string; firstName: string; lastName?: string } & CaptchaInput) =>
    api.post<User>('/auth/register', input),
  verifyEmail: (token: string) => api.post<User>('/auth/verify-email', { token }),
  resendVerification: (email: string, captcha: CaptchaInput = {}) =>
    api.post<{ ok: true }>('/auth/resend-verification', { email, ...captcha }),
  forgotPassword: (email: string, captcha: CaptchaInput = {}) =>
    api.post<{ ok: true }>('/auth/password/forgot', { email, ...captcha }),
  resetPassword: (token: string, password: string) =>
    api.post<{ ok: true }>('/auth/password/reset', { token, password }),
};

// --- Phase 2: contacts, tags, custom fields ---------------------------------

export const TAG_COLORS = ['slate', 'red', 'orange', 'amber', 'green', 'teal', 'blue', 'violet', 'pink'] as const;
export type TagColor = (typeof TAG_COLORS)[number];

export interface Tag {
  id: string;
  name: string;
  color: string;
  description: string | null;
  contactCount: number;
  /** Automations whose live or draft version uses the tag (D-133). */
  automationCount: number;
  createdAt: string;
}

export type CustomFieldType = 'text' | 'number' | 'boolean' | 'date' | 'email' | 'phone' | 'select';

export interface CustomField {
  id: string;
  key: string;
  label: string;
  type: CustomFieldType;
  options: string[];
  sortOrder: number;
  archived: boolean;
  createdAt: string;
}

export type FieldValue = string | number | boolean | null;

export interface Contact {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  phone: string | null;
  avatarUrl: string | null;
  channel: string | null;
  source: string;
  status: string;
  notes: string | null;
  lastInteractionAt: string | null;
  createdAt: string;
  updatedAt: string;
  tags: Array<{ id: string; name: string; color: string }>;
  fields: Record<string, FieldValue>;
}

export interface ContactListFilters {
  q?: string;
  tagIds?: string[];
  channel?: string;
  status?: string;
  fieldId?: string;
  fieldOp?: 'eq' | 'contains' | 'gt' | 'lt';
  fieldValue?: string;
  /** A saved segment (D-100). */
  segmentId?: string;
  sort?: 'createdAt' | 'lastInteractionAt' | 'firstName' | 'lastName' | 'email';
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface ContactList {
  data: Contact[];
  meta: { total: number; page: number; limit: number };
}

export interface ContactWrite {
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  status?: string;
  fields?: Record<string, FieldValue>;
}

export interface ImportResult {
  id: string;
  filename: string;
  status: 'completed' | 'failed';
  totalRows: number;
  importedRows: number;
  skippedRows: number;
  rowErrors: Array<{ row: number; message: string }>;
  columnMapping: Record<string, string>;
}

function contactQueryString(filters: ContactListFilters): string {
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  if (filters.tagIds && filters.tagIds.length > 0) params.set('tagIds', filters.tagIds.join(','));
  if (filters.channel) params.set('channel', filters.channel);
  if (filters.status) params.set('status', filters.status);
  if (filters.fieldId && filters.fieldValue !== undefined) {
    params.set('fieldId', filters.fieldId);
    params.set('fieldOp', filters.fieldOp ?? 'eq');
    params.set('fieldValue', filters.fieldValue);
  }
  if (filters.segmentId) params.set('segmentId', filters.segmentId);
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.order) params.set('order', filters.order);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const contactsApi = {
  list: (filters: ContactListFilters) =>
    requestEnvelope<ContactList>('GET', `/contacts${contactQueryString(filters)}`),
  get: (id: string) => api.get<Contact>(`/contacts/${id}`),
  create: (input: ContactWrite & { tagIds?: string[] }) => api.post<Contact>('/contacts', input),
  update: (id: string, input: ContactWrite) => api.put<Contact>(`/contacts/${id}`, input),
  remove: (id: string) => api.del<{ ok: true }>(`/contacts/${id}`),
  addTags: (id: string, tagIds: string[]) => api.post<Contact>(`/contacts/${id}/tags`, { tagIds }),
  removeTag: (id: string, tagId: string) => api.del<Contact>(`/contacts/${id}/tags/${tagId}`),
  bulkTags: (input: { contactIds: string[]; addTagIds?: string[]; removeTagIds?: string[] }) =>
    api.post<{ matchedContacts: number; added: number; removed: number }>('/contacts/bulk-tags', input),
  import: (input: { filename: string; csv: string; mapping?: Record<string, string> }) =>
    api.post<ImportResult>('/contacts/import', input),
  export: (filters: ContactListFilters) => requestText(`/contacts/export${contactQueryString(filters)}`),
};

// --- contact segments (D-100) -------------------------------------------------------

export type SegmentCondition =
  | { type: 'tag'; op: 'has' | 'not_has'; tagId: string }
  | { type: 'field'; fieldId: string; op: 'eq' | 'contains' | 'gt' | 'lt' | 'is_set' | 'is_not_set'; value?: string }
  | { type: 'channel'; op: 'is' | 'is_not'; channel: string }
  | { type: 'status'; op: 'is' | 'is_not'; status: 'active' | 'unsubscribed' | 'blocked' }
  | { type: 'created'; op: 'within_days' | 'older_than_days'; days: number }
  | { type: 'last_interaction'; op: 'within_days' | 'older_than_days' | 'never'; days?: number };

export interface SegmentDefinition {
  match: 'all' | 'any';
  conditions: SegmentCondition[];
}

export interface Segment extends SegmentDefinition {
  id: string;
  name: string;
  contactCount: number;
  createdAt: string;
  updatedAt: string;
}

/** A comment flow saved by the workspace to reuse on posts (D-104); `flow` is the composer card's form state. */
export interface FlowTemplate {
  id: string;
  name: string;
  flow: {
    preset: 'auto_dm' | 'reply' | 'giveaway' | 'follow_gate' | 'dm_tag' | 'custom';
    match: 'specific_words' | 'any_word';
    keywords: string[];
    publicReply: { enabled: boolean; text: string };
    followGate: { enabled: boolean; askText: string };
    /** Absent on templates saved before D-118. */
    opener?: { text: string; buttonLabel: string };
    dm: { enabled: boolean; text: string; button: { label: string; url: string } | null };
    tag: { enabled: boolean; name: string };
  };
  createdAt: string;
  updatedAt: string;
}

export const flowTemplatesApi = {
  list: () => api.get<FlowTemplate[]>('/flow-templates'),
  create: (input: { name: string; flow: FlowTemplate['flow'] }) => api.post<FlowTemplate>('/flow-templates', input),
  update: (id: string, input: { name: string; flow: FlowTemplate['flow'] }) => api.put<FlowTemplate>(`/flow-templates/${id}`, input),
  remove: (id: string) => api.del<{ ok: true }>(`/flow-templates/${id}`),
};

export const segmentsApi = {
  list: () => api.get<Segment[]>('/segments'),
  preview: (definition: SegmentDefinition) => api.post<{ contactCount: number }>('/segments/preview', definition),
  create: (input: SegmentDefinition & { name: string }) => api.post<Segment>('/segments', input),
  update: (id: string, input: SegmentDefinition & { name: string }) => api.put<Segment>(`/segments/${id}`, input),
  remove: (id: string) => api.del<{ ok: true }>(`/segments/${id}`),
};

export const tagsApi = {
  list: () => api.get<Tag[]>('/tags'),
  create: (input: { name: string; color?: string; description?: string }) => api.post<Tag>('/tags', input),
  update: (id: string, input: { name?: string; color?: string; description?: string | null }) =>
    api.put<Tag>(`/tags/${id}`, input),
  remove: (id: string) => api.del<{ ok: true }>(`/tags/${id}`),
};

export const fieldsApi = {
  list: (includeArchived = false) =>
    api.get<CustomField[]>(`/custom-fields${includeArchived ? '?includeArchived=true' : ''}`),
  create: (input: { label: string; key?: string; type: CustomFieldType; options?: string[] }) =>
    api.post<CustomField>('/custom-fields', input),
  update: (
    id: string,
    input: { label?: string; options?: string[]; sortOrder?: number; archived?: boolean },
  ) => api.put<CustomField>(`/custom-fields/${id}`, input),
  remove: (id: string) => api.del<{ ok: true }>(`/custom-fields/${id}`),
};

// --- Phase 3: automations ----------------------------------------------------

export type NodeKind = 'trigger' | 'action' | 'condition';
export type AutomationStatus = 'draft' | 'active' | 'paused' | 'archived';

export interface GraphNode {
  key: string;
  kind: NodeKind;
  type: string;
  label: string | null;
  config: Record<string, unknown>;
  position: { x: number; y: number };
}

export interface GraphEdge {
  fromKey: string;
  toKey: string;
  branch: string;
  label: string | null;
}

export interface Graph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface ValidationIssue {
  nodeKey: string | null;
  path: string | null;
  code: string;
  message: string;
}

export interface AutomationSummary {
  id: string;
  name: string;
  description: string | null;
  status: AutomationStatus;
  isEnabled: boolean;
  draftVersion: number | null;
  liveVersion: number | null;
  triggerType: string | null;
  /** The guided form (QUICK), a template, or the blank canvas (D-133). */
  origin: 'quick' | 'template' | 'builder';
  /** When its latest run started; null before the first (D-133). */
  lastRunAt: string | null;
  /** 'mcp' for a draft an AI client made through the MCP server (D-181); null otherwise. */
  createdVia: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * What a guided form tells the server it made (D-133); templates go through
 * `templatesApi.instantiate`. A copy of the server's list — a test holds the
 * two together (D-134).
 */
export const QUICK_TEMPLATE_KEYS = ['quick:comment', 'quick:dm', 'quick:story', 'post:comment_flow'] as const;
export type QuickTemplateKey = (typeof QUICK_TEMPLATE_KEYS)[number];

export interface ConversionGoal {
  type: 'tag_added';
  tagId: string;
  windowHours: number;
}

export interface AutomationDetail extends AutomationSummary {
  graph: Graph;
  issues: ValidationIssue[];
  conversionGoal: ConversionGoal | null;
  /** The draft runs differently from the live version (D-132); false while nothing is live. */
  hasUnpublishedChanges: boolean;
}

/** Save payload: `kind` is derived server-side and edge branches default to "default". */
export interface GraphWrite {
  nodes: Array<{
    key: string;
    type: string;
    label?: string | null;
    config: Record<string, unknown>;
    position: { x: number; y: number };
  }>;
  edges: Array<{ fromKey: string; toKey: string; branch?: string; label?: string | null }>;
}

export type TemplateGoal = 'grow' | 'engage' | 'traffic' | 'leads' | 'sell';

export interface AutomationTemplate {
  key: string;
  name: string;
  description: string;
  category: string;
  /** D-101: what the template helps with, and the trigger it starts from. */
  goals: TemplateGoal[];
  /** The detail view's "Business value" and "How does it work?". */
  businessValue: string;
  howItWorks: string;
  triggerType: string | null;
  /** The flow's steps in run order, for the card's chips (D-133). */
  steps: Array<{ type: string; kind: NodeKind }>;
  requiredTags: string[];
  /** `quick`: set up through a guided form; absent = opens in the Flow Builder (D-101). */
  setup?: 'quick' | 'builder';
}

export const automationsApi = {
  list: () => api.get<AutomationSummary[]>('/automations'),
  get: (id: string) => api.get<AutomationDetail>(`/automations/${id}`),
  create: (input: { name: string; description?: string; templateKey?: QuickTemplateKey }) =>
    api.post<AutomationDetail>('/automations', input),
  update: (id: string, input: { name?: string; description?: string | null; conversionGoal?: ConversionGoal | null }) =>
    api.put<AutomationDetail>(`/automations/${id}`, input),
  remove: (id: string) => api.del<{ ok: true }>(`/automations/${id}`),
  duplicate: (id: string) => api.post<AutomationDetail>(`/automations/${id}/duplicate`),
  saveGraph: (id: string, graph: GraphWrite) =>
    api.put<{ graph: Graph; issues: ValidationIssue[] }>(`/automations/${id}/graph`, graph),
  activate: (id: string) => api.post<AutomationDetail>(`/automations/${id}/activate`),
  deactivate: (id: string) => api.post<AutomationDetail>(`/automations/${id}/deactivate`),
  /** SPEC §21: runs the live version for one real contact, through the real engine. */
  testRun: (id: string, contactId: string) =>
    api.post<{ executionId: string | null; execution: ExecutionDetail | null }>(`/automations/${id}/test`, { contactId }),
};

export interface ExecutionStep {
  sequence: number;
  attempt: number;
  nodeKey: string;
  nodeType: string;
  status: 'running' | 'succeeded' | 'failed' | 'skipped' | 'waiting';
  branchTaken: string | null;
  output: unknown;
  error: unknown;
  scheduledFor: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
}

export interface ExecutionDetail {
  id: string;
  status: 'pending' | 'running' | 'waiting' | 'completed' | 'failed' | 'canceled';
  contact: { id: string; name: string };
  endReason: string | null;
  error: unknown;
  stepsExecuted: number;
  resumeAt: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  automationVersion: number | null;
  steps: ExecutionStep[];
}

export const templatesApi = {
  list: () => api.get<AutomationTemplate[]>('/automation-templates'),
  instantiate: (key: string) => api.post<AutomationDetail>(`/automation-templates/${key}/instantiate`),
};

// --- Phase 5: inbox ------------------------------------------------------------

export interface ConversationContact {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  phone: string | null;
  avatarUrl: string | null;
}

export interface ConversationSummary {
  id: string;
  status: 'open' | 'closed';
  unreadCount: number;
  channel: string;
  channelAccountName: string | null;
  preview: string | null;
  lastMessageAt: string | null;
  lastInboundAt: string | null;
  automationPausedUntil: string | null;
  automationPaused: boolean;
  closedAt: string | null;
  createdAt: string;
  contact: ConversationContact;
  assignee: { userId: string; firstName: string; lastName: string | null; email: string } | null;
  /** The automation that sent the latest outbound message; null once an agent replies (D-133). */
  handledBy: { automationId: string; automationName: string; repliedAt: string } | null;
}

export interface ConversationDetail extends ConversationSummary {
  contactPanel: {
    notes: string | null;
    source: string;
    contactStatus: string;
    createdAt: string;
    lastInteractionAt: string | null;
    tags: Array<{ id: string; name: string; color: string }>;
  } | null;
  activeAutomations: Array<{
    executionId: string;
    automationId: string;
    automationName: string;
    status: string;
    resumeAt: string | null;
    startedAt: string;
  }>;
}

export interface InboxMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  senderType: 'contact' | 'agent' | 'automation' | 'system';
  senderName: string | null;
  text: string | null;
  content: unknown;
  status: string;
  automationExecutionId: string | null;
  /** The automation whose run sent it, deleted ones included (D-133). */
  automation: { id: string; name: string } | null;
  createdAt: string;
}

export interface ConversationListFilters {
  status?: 'open' | 'closed' | 'all';
  assigned?: 'me' | 'unassigned';
  unread?: boolean;
  q?: string;
  page?: number;
  limit?: number;
}

export interface ConversationList {
  data: ConversationSummary[];
  meta: { total: number; page: number; limit: number };
}

export interface MessagePage {
  data: InboxMessage[];
  meta: { nextCursor: string | null };
}

function conversationQueryString(filters: ConversationListFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.assigned) params.set('assigned', filters.assigned);
  if (filters.unread) params.set('unread', 'true');
  if (filters.q) params.set('q', filters.q);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const inboxApi = {
  list: (filters: ConversationListFilters) =>
    requestEnvelope<ConversationList>('GET', `/conversations${conversationQueryString(filters)}`),
  get: (id: string) => api.get<ConversationDetail>(`/conversations/${id}`),
  messages: (id: string, options: { before?: string; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (options.before) params.set('before', options.before);
    if (options.limit) params.set('limit', String(options.limit));
    const qs = params.toString();
    return requestEnvelope<MessagePage>('GET', `/conversations/${id}/messages${qs ? `?${qs}` : ''}`);
  },
  reply: (id: string, input: { text: string; clientMessageId?: string }) =>
    api.post<InboxMessage>(`/conversations/${id}/messages`, input),
  markRead: (id: string) => api.post<{ ok: true }>(`/conversations/${id}/read`),
  assign: (id: string, userId: string | null) =>
    api.post<ConversationSummary>(`/conversations/${id}/assign`, { userId }),
  close: (id: string) => api.post<ConversationSummary>(`/conversations/${id}/close`),
  reopen: (id: string) => api.post<ConversationSummary>(`/conversations/${id}/reopen`),
  pauseAutomations: (id: string, minutes?: number) =>
    api.post<ConversationSummary>(`/conversations/${id}/automations/pause`, minutes === undefined ? {} : { minutes }),
  resumeAutomations: (id: string) => api.post<ConversationSummary>(`/conversations/${id}/automations/resume`),
};

// --- Phase 9: analytics, usage, activity --------------------------------------

export interface DateRangeParams {
  from?: string;
  to?: string;
}

function rangeQueryString(range: DateRangeParams): string {
  const params = new URLSearchParams();
  if (range.from) params.set('from', range.from);
  if (range.to) params.set('to', range.to);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export interface DashboardMetrics {
  totalContacts: number;
  newContacts: number;
  activeAutomations: number;
  messagesSent: number;
  messagesReceived: number;
  executions: number;
  recentConversations: Array<{
    id: string;
    contactName: string;
    preview: string | null;
    status: string;
    unreadCount: number;
    lastMessageAt: string | null;
  }>;
  recentActivity: Array<{ id: string; type: string; status: string; message: string; createdAt: string }>;
}

export interface AutomationAnalyticsSummary {
  automationId: string;
  name: string;
  status: string;
  executionsTotal: number;
  executionsCompleted: number;
  executionsFailed: number;
  messagesSent: number;
  contactsReached: number;
  conversions: number | null;
  conversionRate: number | null;
}

export interface AutomationAnalyticsDetail extends AutomationAnalyticsSummary {
  conversionGoal: ConversionGoal | null;
  series: Array<{ day: string; executions: number; completed: number; failed: number; messagesSent: number }>;
}

export const analyticsApi = {
  dashboard: (range: DateRangeParams) => api.get<DashboardMetrics>(`/analytics/dashboard${rangeQueryString(range)}`),
  automations: (range: DateRangeParams) =>
    api.get<AutomationAnalyticsSummary[]>(`/analytics/automations${rangeQueryString(range)}`),
  automation: (id: string, range: DateRangeParams) =>
    api.get<AutomationAnalyticsDetail>(`/analytics/automations/${id}${rangeQueryString(range)}`),
};

export type PlanLimitKey =
  | 'maxContacts'
  | 'maxUsers'
  | 'maxChannels'
  | 'maxInboxSeats'
  | 'maxAutomations'
  | 'maxActiveAutomations'
  | 'maxExecutionsPerMonth'
  | 'maxMessagesPerMonth'
  | 'maxCustomFields'
  /** How far back analytics may be read, in days (D-183). */
  | 'maxAnalyticsDays';

export interface UsageSummary {
  plan: { code: string; name: string };
  limits: Record<PlanLimitKey, number | null>;
  usage: {
    contacts: number;
    users: number;
    pendingInvites: number;
    channels: number;
    inboxSeats: number;
    automations: number;
    activeAutomations: number;
    customFields: number;
    executionsThisMonth: number;
    messagesThisMonth: number;
  };
}

/** An invoice as the workspace sees it (D-107). Drafts are never returned. */
export interface TenantInvoice {
  id: string;
  number: string;
  amount: number;
  paidAmount: number;
  amountDue: number;
  currency: string;
  status: 'issued' | 'overdue' | 'paid' | 'void';
  periodStart: string;
  periodEnd: string;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
}

export interface TenantInvoices {
  /** Still to pay — drives the in-app notice, which disappears once this is empty. */
  open: TenantInvoice[];
  recent: TenantInvoice[];
  paymentInstructions: string | null;
}

/** The plan's on/off entitlements (D-183); `false` = refused by the server with a 402 `FEATURE_NOT_IN_PLAN`. */
export type PlanFeatureKey = 'scheduledPosts' | 'campaigns' | 'segments' | 'httpRequestStep' | 'apiAccess';
export type PlanFeatures = Record<PlanFeatureKey, boolean>;
export type BillingInterval = 'month' | 'quarter' | 'year';
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'suspended' | 'canceled';

/** A plan change the owner asked for (D-183): an upgrade waits for payment, a downgrade for the period's end. */
export interface PlanRequest {
  id: string;
  kind: 'upgrade' | 'downgrade';
  status: 'pending' | 'scheduled' | 'applied' | 'rejected' | 'canceled';
  requestedPlan: { id: string; code: string; name: string; priceAmount: number; currency: string; interval: BillingInterval };
  requestedAt: string;
  effectiveAt: string | null;
  /** Only in the answer to a request applied at once (`applied`, amended 2026-10-31): the end of the period the credit pays. */
  coveredUntil: string | null;
}

/** One paid plan a tenant may ask for, as `GET /billing/plan` lists them (cheapest first). */
export interface CatalogueEntry {
  id: string;
  code: string;
  name: string;
  description: string | null;
  priceAmount: number;
  currency: string;
  interval: BillingInterval;
  /** Display copy (D-066). */
  features: string[];
  limits: Record<PlanLimitKey, number | null>;
  flags: PlanFeatures;
}

/**
 * What asking for a plan would do (D-183, amended 2026-10-31), computed by the
 * server function the back-office's confirmation uses. For an upgrade: the
 * credit of the paid period, the amount to pay and the first period — a
 * credit that covers the price pays 0 and lengthens the period.
 */
export interface PlanRequestPreview {
  kind: 'upgrade' | 'downgrade';
  effectiveAt: string | null;
  proration: {
    credit: number;
    amount: number;
    coveredByCredit: boolean;
    currency: string;
    periodStart: string;
    periodEnd: string;
  } | null;
  /** The credit of a settled period pays the whole plan: asking applies it at once, no back-office (amended 2026-10-31). */
  appliesAtOnce: boolean;
}

/** `POST /billing/owner-requests`: sent now, or already sent by this member within the day. */
export interface OwnerRequest {
  status: 'sent' | 'already_sent';
  requestedAt: string;
}

/** "Mon offre" (D-183): owner and admins only; still answers while the workspace is suspended. */
export interface PlanOverview {
  current: {
    planId: string;
    code: string;
    name: string;
    priceAmount: number;
    currency: string;
    interval: BillingInterval;
    status: SubscriptionStatus;
    trialEndsAt: string | null;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    /** « Offert » (D-183, 2026-10-31): nothing to pay, no renewal; the plan's limits apply. */
    complimentary: boolean;
    /** Null = no end date. */
    complimentaryEndsAt: string | null;
  };
  suspendedAt: string | null;
  suspensionReason: string | null;
  features: PlanFeatures;
  catalogue: CatalogueEntry[];
  openRequest: PlanRequest | null;
  /** The current plan was applied at once because the credit paid it, and that first period still runs: its end. */
  creditCoveredUntil: string | null;
  /** True for the owner only: billing is the owner's (D-021). */
  canRequest: boolean;
  paymentInstructions: string | null;
}

export const billingApi = {
  usage: () => api.get<UsageSummary>('/billing/usage'),
  /** Owner/admin only (403 for agents); still answers while the workspace is suspended. */
  plan: () => api.get<PlanOverview>('/billing/plan'),
  /** Owner only. An upgrade comes back `pending`, a downgrade `scheduled` (D-183). */
  requestPlan: (planId: string) => api.post<PlanRequest>('/billing/plan-requests', { planId }),
  cancelPlanRequest: (requestId: string) => api.del<PlanRequest>(`/billing/plan-requests/${requestId}`),
  /** Owner/admin only, read-only: the credit and the amount a plan change would apply now. */
  previewPlanRequest: (planId: string) =>
    api.get<PlanRequestPreview>(`/billing/plan-requests/preview?planId=${encodeURIComponent(planId)}`),
  /**
   * Admin or agent: asks the owner by e-mail to open `feature` (D-183). The
   * owner gets a 403; at most one e-mail per member and feature a day.
   */
  askOwner: (feature: PlanFeatureKey) => api.post<OwnerRequest>('/billing/owner-requests', { feature }),
  /** Owner/admin only (403 for agents); still answers while the workspace is suspended. */
  invoices: () => api.get<TenantInvoices>('/billing/invoices'),
  /** The invoice as a PDF download. Rejects with an ApiError (503 while the issuer is not configured). */
  invoicePdf: (invoiceId: string) => downloadFile(`/billing/invoices/${invoiceId}/pdf`, 'facture.pdf'),
};

export interface ActivityEntry {
  id: string;
  type: string;
  status: string;
  actorType: string;
  message: string;
  contactId: string | null;
  conversationId: string | null;
  automationId: string | null;
  executionId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface ActivityListFilters extends DateRangeParams {
  contactId?: string;
  automationId?: string;
  type?: string;
  status?: 'success' | 'failure' | 'info';
  page?: number;
  limit?: number;
}

export const activityApi = {
  list: (filters: ActivityListFilters) => {
    const params = new URLSearchParams();
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (filters.contactId) params.set('contactId', filters.contactId);
    if (filters.automationId) params.set('automationId', filters.automationId);
    if (filters.type) params.set('type', filters.type);
    if (filters.status) params.set('status', filters.status);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return requestEnvelope<{ data: ActivityEntry[]; meta: { total: number; page: number; limit: number } }>(
      'GET',
      `/activity-logs${qs ? `?${qs}` : ''}`,
    );
  },
};

// --- Phases 7–8: channel accounts ---------------------------------------------

export interface ChannelAccount {
  id: string;
  channelCode: string;
  externalAccountId: string;
  displayName: string;
  avatarUrl: string | null;
  status: string;
  scopes: string[];
  tokenExpiresAt: string | null;
  lastError: string | null;
  createdAt: string;
  /** Permissions the server asks for today that this account never granted; a reconnect grants them. */
  missingScopes: { scope: string; label: string }[];
}

/** One post on a connected account — feeds the comment trigger's post picker. */
export interface ChannelPost {
  id: string;
  caption: string | null;
  mediaType: string | null;
  thumbnailUrl: string | null;
  permalink: string | null;
  publishedAt: string | null;
}

/** One story currently up on a connected account — feeds the story reply trigger's picker (D-117). */
export interface ChannelStory {
  id: string;
  mediaType: string | null;
  thumbnailUrl: string | null;
  permalink: string | null;
  publishedAt: string | null;
}

export const channelAccountsApi = {
  list: () => api.get<ChannelAccount[]>('/channel-accounts'),
  /** Returns the provider's OAuth URL; the caller navigates the browser there. */
  connect: (channelCode: string) =>
    api.post<{ authorizationUrl: string }>(`/channel-accounts/${channelCode}/connect`),
  disconnect: (accountId: string) => api.del<{ ok: true }>(`/channel-accounts/${accountId}`),
  listPosts: (accountId: string) => api.get<ChannelPost[]>(`/channel-accounts/${accountId}/posts`),
  listStories: (accountId: string) => api.get<ChannelStory[]>(`/channel-accounts/${accountId}/stories`),
  menu: (accountId: string) => api.get<ChannelMenu>(`/channel-accounts/${accountId}/menu`),
  saveMenu: (accountId: string, input: { starters: ChannelMenuItemInput[]; menu: ChannelMenuItemInput[] }) =>
    api.put<ChannelMenu>(`/channel-accounts/${accountId}/menu`, input),
  /** The accounts a parked multi-account grant reaches (D-139); the grant itself is an HttpOnly cookie. */
  grant: (channelCode: string) => api.get<ConnectGrant>(`/channel-accounts/${channelCode}/grant`),
  connectGrant: (channelCode: string, externalAccountIds: string[]) =>
    api.post<ChannelAccount[]>(`/channel-accounts/${channelCode}/grant`, { externalAccountIds }),
  dropGrant: (channelCode: string) => api.del<{ ok: true }>(`/channel-accounts/${channelCode}/grant`),
};

/** One account a parked grant can connect, as the picker shows it (D-139). */
export interface GrantAccount {
  externalAccountId: string;
  displayName: string;
  avatarUrl: string | null;
  /** Already connected in this workspace: ticking it refreshes it and takes no new slot. */
  connectedHere: boolean;
  /** Connected in another workspace: cannot be picked here (D-085). */
  heldElsewhere: boolean;
}

export interface ConnectGrant {
  channelCode: string;
  accounts: GrantAccount[];
  /** The plan's channel limit, `null` when unlimited. */
  limit: number | null;
  /** Accounts connected in the workspace now, all channels. */
  connected: number;
}

// --- conversation starters + menu (D-099) ---------------------------------------

export type ChannelMenuAction = 'automation' | 'url';

export interface ChannelMenuItemInput {
  title: string;
  action: ChannelMenuAction;
  automationId?: string | null;
  url?: string | null;
}

export interface ChannelMenuItem {
  id: string;
  title: string;
  action: ChannelMenuAction;
  automationId: string | null;
  automationName: string | null;
  url: string | null;
}

export interface ChannelMenu {
  supported: boolean;
  starters: ChannelMenuItem[];
  menu: ChannelMenuItem[];
  syncedAt: string | null;
  syncError: string | null;
}

// --- scheduled posts (D-092) -------------------------------------------------

export interface MediaAsset {
  id: string;
  kind: 'image' | 'video';
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  originalName: string | null;
  /** Same-origin path, usable directly as an img/video src. */
  url: string;
  createdAt: string;
}

export interface PostUserTag {
  username: string;
  x: number;
  y: number;
}

/** A story (D-136) is one photo or video, up for 24 hours, with no caption. */
export type PostType = 'image' | 'carousel' | 'reel' | 'story';
export type PostStatus = 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'canceled';

/** A post's copy on another account (D-143), as its original shows it. */
export interface CrossPost {
  id: string;
  channelAccount: { id: string; channelCode: string; displayName: string; avatarUrl: string | null };
  status: PostStatus;
  scheduledAt: string | null;
  publishedAt: string | null;
  permalink: string | null;
  lastError: string | null;
}

export interface ScheduledPost {
  id: string;
  channelAccount: { id: string; channelCode: string; displayName: string; avatarUrl: string | null };
  campaign: { id: string; name: string; status: string } | null;
  /** The post this one is the copy of (D-143): it is edited through that one while that one can be. */
  crossPostOfId: string | null;
  /** "Also post on" (D-143): this post's copy on another account. */
  crossPost: CrossPost | null;
  type: PostType;
  status: PostStatus;
  caption: string;
  hashtags: string[];
  collaborators: string[];
  shareToFeed: boolean;
  cover: MediaAsset | null;
  items: Array<{ id: string; position: number; asset: MediaAsset; userTags: PostUserTag[]; altText: string | null }>;
  scheduledAt: string | null;
  publishedAt: string | null;
  permalink: string | null;
  externalMediaId: string | null;
  lastError: string | null;
  attempt: number;
  /** Automations whose comment trigger waits for this post (D-103). */
  automations: Array<{ id: string; name: string; status: AutomationStatus }>;
  /** Instagram's latest numbers and the sync state (D-115); null until published. */
  insights: PostInsights | null;
  createdAt: string;
  updatedAt: string;
}

/** Instagram's numbers for a published post (D-115); null = not reported. */
export interface InsightMetrics {
  likes: number | null;
  comments: number | null;
  views: number | null;
  reach: number | null;
  saved: number | null;
  shares: number | null;
  totalInteractions: number | null;
  /** Reels only. */
  avgWatchTimeMs: number | null;
}

export type InsightsErrorCode = 'INSIGHTS_SCOPE_MISSING' | 'ACCOUNT_NOT_CONNECTED' | 'INSIGHTS_UNAVAILABLE' | 'MEDIA_GONE' | 'SYNC_FAILED';

export interface PostInsights {
  /** Last successful sync; null until the first one. */
  syncedAt: string | null;
  capturedAt: string | null;
  /** Why the last sync did not fully succeed; the numbers may be partial. */
  error: { code: InsightsErrorCode | string; message: string } | null;
  metrics: InsightMetrics | null;
}

export interface PostAutomationStats {
  comments: number;
  triggered: number;
  executions: number;
  messagesSent: number;
  contactsReached: number;
}

export interface PostInsightsDetail extends PostInsights {
  /** Newest first. */
  history: Array<InsightMetrics & { day: string }>;
  automation: PostAutomationStats;
}

/** Totals of a campaign's published posts; null = no post reported it. */
export interface CampaignInsights {
  posts: number;
  likes: number | null;
  comments: number | null;
  views: number | null;
  reach: number | null;
  saved: number | null;
  shares: number | null;
  totalInteractions: number | null;
}

export interface PublishingAccount {
  id: string;
  channelCode: string;
  displayName: string;
  avatarUrl: string | null;
  status: string;
  canPublish: boolean;
  code: string;
  reason: string | null;
}

export interface PostInput {
  channelAccountId: string;
  type: PostType;
  items: Array<{ mediaAssetId: string; userTags: PostUserTag[]; altText?: string | null }>;
  caption: string;
  hashtags: string[];
  collaborators: string[];
  shareToFeed: boolean;
  coverAssetId?: string | null;
  campaignId?: string | null;
  /** ISO instant; null saves a draft. */
  scheduledAt: string | null;
  /** "Also post on" another account (D-143); null = no copy. */
  crossPostAccountId?: string | null;
}

export const postsApi = {
  list: () => api.get<ScheduledPost[]>('/posts'),
  get: (id: string) => api.get<ScheduledPost>(`/posts/${id}`),
  publishingAccounts: () => api.get<PublishingAccount[]>('/posts/publishing-accounts'),
  create: (input: PostInput) => api.post<ScheduledPost>('/posts', input),
  update: (id: string, input: PostInput) => api.put<ScheduledPost>(`/posts/${id}`, input),
  cancel: (id: string) => api.post<ScheduledPost>(`/posts/${id}/cancel`),
  retry: (id: string) => api.post<ScheduledPost>(`/posts/${id}/retry`),
  /** Deleting a post pauses the live automations pinned to it; they come back in the answer. */
  remove: (id: string) => api.del<{ ok: true; pausedAutomations: ScheduledPost['automations'] }>(`/posts/${id}`),
  insights: (id: string) => api.get<PostInsightsDetail>(`/posts/${id}/insights`),
  /** Asks Instagram now; a refresh within two minutes of a sync answers from that sync. */
  refreshInsights: (id: string) => api.post<PostInsights>(`/posts/${id}/insights/refresh`),
};

/**
 * Multipart upload with progress. `fetch` cannot report upload progress, and a
 * Reel can be 300 MB, so this one call uses XMLHttpRequest. Same credentials,
 * CSRF header and error shape as every other call.
 */
function uploadFile(file: Blob, filename: string, onProgress?: (fraction: number) => void): Promise<MediaAsset> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('file', file, filename);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/media');
    xhr.withCredentials = true;
    xhr.setRequestHeader('X-Requested-With', 'chatflow-web');
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () => {
      type UploadPayload = { data?: MediaAsset; error?: ApiErrorBody['error'] };
      let payload: UploadPayload | null = null;
      try {
        payload = JSON.parse(xhr.responseText) as UploadPayload;
      } catch {
        payload = null;
      }
      if (xhr.status >= 200 && xhr.status < 300 && payload?.data) {
        resolve(payload.data);
        return;
      }
      const err = payload?.error;
      reject(
        new ApiError(
          xhr.status,
          err?.code ?? (xhr.status === 413 ? 'FILE_TOO_LARGE' : 'UNKNOWN'),
          err?.message ?? i18n.t(xhr.status === 413 ? 'common.uploadTooLarge' : 'common.uploadFailed'),
          err?.details,
        ),
      );
    };
    xhr.onerror = () => reject(new ApiError(0, 'NETWORK', i18n.t('common.uploadOffline')));
    xhr.send(form);
  });
}

export const mediaApi = {
  list: () => api.get<MediaAsset[]>('/media'),
  upload: uploadFile,
  remove: (id: string) => api.del<{ ok: true }>(`/media/${id}`),
};

// --- campaigns (D-094) ------------------------------------------------------------

export type CampaignStatus = 'draft' | 'active' | 'paused' | 'canceled' | 'completed';

export interface Campaign {
  id: string;
  name: string;
  description: string | null;
  status: CampaignStatus;
  launchedAt: string | null;
  nextPostAt: string | null;
  counts: { total: number; planned: number; scheduled: number; publishing: number; published: number; failed: number; canceled: number };
  createdAt: string;
  updatedAt: string;
}

export interface CampaignDetail extends Campaign {
  posts: ScheduledPost[];
  /** Totals of the published posts' latest Instagram numbers (D-115). */
  insights: CampaignInsights;
}

export const campaignsApi = {
  list: () => api.get<Campaign[]>('/campaigns'),
  get: (id: string) => api.get<CampaignDetail>(`/campaigns/${id}`),
  create: (input: { name: string; description?: string | null }) => api.post<CampaignDetail>('/campaigns', input),
  update: (id: string, input: { name?: string; description?: string | null }) => api.put<CampaignDetail>(`/campaigns/${id}`, input),
  launch: (id: string) => api.post<CampaignDetail>(`/campaigns/${id}/launch`),
  pause: (id: string) => api.post<CampaignDetail>(`/campaigns/${id}/pause`),
  cancel: (id: string) => api.post<CampaignDetail>(`/campaigns/${id}/cancel`),
  remove: (id: string) => api.del<{ ok: true }>(`/campaigns/${id}`),
};

export const workspaceApi = {
  list: () => api.get<Workspace[]>('/workspaces'),
  current: () => api.get<CurrentWorkspace>('/workspaces/current'),
  create: (name: string, timezone?: string) => api.post<Workspace>('/workspaces', { name, timezone }),
  activate: (id: string) => api.post<Workspace>(`/workspaces/${id}/activate`),
  members: () => api.get<Member[]>('/workspaces/current/members'),
  invites: () => api.get<Invite[]>('/workspaces/current/invites'),
  invite: (email: string, role: 'admin' | 'agent') =>
    api.post<Invite>('/workspaces/current/invites', { email, role }),
  revokeInvite: (id: string) => api.del<{ ok: true }>(`/workspaces/current/invites/${id}`),
  previewInvite: (token: string) => api.post<InvitePreview>('/workspaces/invites/preview', { token }),
  acceptInvite: (token: string) => api.post<Workspace>('/workspaces/invites/accept', { token }),
  inboxSettings: () => api.get<InboxSettings>('/workspaces/current/inbox-settings'),
  updateInboxSettings: (input: { agentPauseMinutes: number | null }) =>
    api.put<InboxSettings>('/workspaces/current/inbox-settings', input),
};

/** D-098: how long an agent reply pauses automations; null = deployment default, 0 = never. */
export interface InboxSettings {
  agentPauseMinutes: number | null;
  defaultAgentPauseMinutes: number;
}

// --- API tokens for the MCP server (D-180) -----------------------------------

export type ApiTokenScope = 'read' | 'write';

export type ApiTokenLifetimeDays = 30 | 90 | 365;

/** The server's `ApiTokenDto`: never the token itself nor its hash. */
export interface ApiToken {
  id: string;
  name: string;
  /** `rpq_` and the first six characters, for recognising a token in the list. */
  prefix: string;
  scopes: ApiTokenScope[];
  createdBy: { id: string; firstName: string; lastName: string | null };
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
  revokedAt: string | null;
}

/** The creation's answer: the plaintext token, returned this once only. */
export interface ApiTokenCreated {
  token: string;
  apiToken: ApiToken;
}

/** Admin and above, the list included (D-180). */
export const apiTokensApi = {
  list: () => api.get<ApiToken[]>('/api-tokens'),
  create: (input: { name: string; scopes: ApiTokenScope[]; expiresInDays: ApiTokenLifetimeDays }) =>
    api.post<ApiTokenCreated>('/api-tokens', input),
  revoke: (id: string) => api.del<ApiToken>(`/api-tokens/${id}`),
};
